import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { usePushMessages } from '@/hooks/useNotificacoes'
import {
    Send, Bell, History, Plus, Search, MessageSquare,
    Filter, Loader2, ArrowUpRight, CheckCircle2, AlertCircle, Clock
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { SendNotificationForm } from '@/components/admin/SendNotificationForm'

export function NotificacoesPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [showSendForm, setShowSendForm] = useState(false)

    useEffect(() => { setPageTitle('Comunicações & Push') }, [setPageTitle])

    const { data: messages, isLoading } = usePushMessages()

    const stats = [
        { label: 'Enviadas', value: messages?.length || 0, icon: Send, color: 'text-primary-600', bg: 'bg-primary-50' },
        { label: 'Lidas', value: '85%', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
        { label: 'Falhas', value: 0, icon: AlertCircle, color: 'text-danger-600', bg: 'bg-danger-50' },
        { label: 'Agendadas', value: 0, icon: Clock, color: 'text-warning-600', bg: 'bg-warning-50' },
    ]

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Comunicações</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Gerencie envios de WhatsApp, Push e Informativos</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/admin/notificacoes/templates" className="btn border border-surface-200 bg-white text-surface-700 gap-2">
                        <MessageSquare size={18} /> Templates
                    </Link>
                    <Link to="/admin/notificacoes/noticias" className="btn border border-surface-200 bg-white text-surface-700 gap-2">
                        <Bell size={18} /> Notícias Portal
                    </Link>
                    <button onClick={() => setShowSendForm(true)} className="btn btn-primary gap-2">
                        <Plus size={18} /> Novo Envio
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                    <div key={i} className="glass-card p-4 flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${stat.bg} ${stat.color}`}>
                            <stat.icon size={24} />
                        </div>
                        <div>
                            <p className="text-xs font-medium text-surface-700/50 uppercase tracking-wider">{stat.label}</p>
                            <p className="text-2xl font-bold text-surface-900 leading-tight">{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* History Table */}
            <div className="glass-card">
                <div className="p-4 border-b border-surface-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <History size={18} className="text-surface-700/40" />
                        <h3 className="font-bold text-surface-900">Histórico de Envios</h3>
                    </div>

                    <div className="flex gap-2">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                            <input
                                type="text" placeholder="Buscar mensagens..."
                                className="input pl-9 w-64 h-10 text-sm"
                                value={search} onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <button className="btn p-2 border border-surface-200 bg-white hover:bg-surface-50 text-surface-700">
                            <Filter size={20} />
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto text-sm">
                    {isLoading ? (
                        <div className="flex justify-center p-12">
                            <Loader2 size={24} className="animate-spin text-primary-600" />
                        </div>
                    ) : messages?.length === 0 ? (
                        <div className="text-center py-16 text-surface-700/40">
                            <Send size={40} className="mx-auto mb-3 opacity-20" />
                            <p>Nenhuma mensagem enviada ainda.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-surface-50/50 text-surface-700/60 uppercase text-[10px] font-bold tracking-widest">
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Mensagem</th>
                                    <th className="px-6 py-4">Canais</th>
                                    <th className="px-6 py-4">Data/Hora</th>
                                    <th className="px-6 py-4 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-100">
                                {messages?.map((msg) => (
                                    <tr key={msg.id} className="hover:bg-surface-50/50 transition-colors group">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase border
                                                ${msg.status === 'enviado' ? 'bg-green-50 text-green-700 border-green-100' :
                                                    msg.status === 'erro' ? 'bg-danger-50 text-danger-700 border-danger-100' :
                                                        'bg-warning-50 text-warning-700 border-warning-100'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${msg.status === 'enviado' ? 'bg-green-500' : 'bg-current'}`} />
                                                {msg.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="max-w-xs xl:max-w-md">
                                                <p className="font-semibold text-surface-900 truncate">{msg.titulo}</p>
                                                <p className="text-xs text-surface-700/60 truncate mt-0.5">{msg.corpo}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-1">
                                                {msg.canal.map(c => (
                                                    <span key={c} className="bg-surface-100 text-surface-700 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold">
                                                        {c.replace('_', ' ')}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-surface-900 font-medium">
                                                {format(new Date(msg.enviado_at || msg.created_at), "dd 'de' MMM", { locale: ptBR })}
                                            </p>
                                            <p className="text-[10px] text-surface-700/50">
                                                {format(new Date(msg.enviado_at || msg.created_at), "HH:mm")}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button className="p-2 rounded-lg hover:bg-surface-100 text-surface-700/40 hover:text-primary-600 transition-colors">
                                                <ArrowUpRight size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            {/* Modal Send Form */}
            {showSendForm && (
                <SendNotificationForm onClose={() => setShowSendForm(false)} />
            )}
        </div>
    )
}
